/** 门户图片排版：展示 HTML 构建与兜底修复 */

import { parseHtmlFragment, relocateInlineStylesInHtml } from '@/components/shared/utils/promoteInlineStyles'

export const IMAGE_GALLERY_TYPE = 'image-gallery'
const DEFAULT_GAP = 8
const DEFAULT_ROW_HEIGHT = 220
const DEFAULT_COLUMNS = 3
const MIN_COLUMNS = 2
const MAX_COLUMNS = 4

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function round2(value) {
  return Math.round(Number(value) * 100) / 100
}

function normalizeMode(value) {
  return value === 'justified' ? 'justified' : 'uniform'
}

function normalizeGap(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return DEFAULT_GAP
  return Math.min(40, Math.max(0, Math.round(n)))
}

function normalizeRowHeight(value, fallback = DEFAULT_ROW_HEIGHT) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.min(800, Math.max(80, Math.round(n)))
}

function normalizeSrc(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeItem(value = {}) {
  const widthPct = Number(value.widthPct)
  const heightPx = Number(value.heightPx)
  return {
    src: normalizeSrc(value.src),
    alt: typeof value.alt === 'string' ? value.alt : '',
    widthPct: Number.isFinite(widthPct) && widthPct > 0 ? round2(widthPct) : 0,
    naturalWidth: Math.max(0, Math.round(Number(value.naturalWidth) || 0)),
    naturalHeight: Math.max(0, Math.round(Number(value.naturalHeight) || 0)),
    widthLocked: value.widthLocked === true || value.widthLocked === 'true',
    heightPx: Number.isFinite(heightPx) && heightPx > 0 ? normalizeRowHeight(heightPx) : 0,
    heightLocked: value.heightLocked === true || value.heightLocked === 'true',
  }
}

function normalizeColumnCount(value) {
  const n = Math.round(Number(value) || 0)
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_COLUMNS
  return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, n))
}

function flattenItems(rows = []) {
  return (Array.isArray(rows) ? rows : []).flatMap((row) => (
    Array.isArray(row?.items) ? row.items : []
  )).map(normalizeItem).filter((item) => item.src)
}

function normalizeRowWidths(items) {
  const list = (Array.isArray(items) && items.length ? items : []).map(normalizeItem).filter((item) => item.src)
  if (!list.length) return []
  const sum = list.reduce((total, item) => total + item.widthPct, 0)
  if (sum <= 0) {
    const even = round2(100 / list.length)
    return list.map((item, index) => ({
      ...item,
      widthPct: index === list.length - 1 ? round2(100 - even * (list.length - 1)) : even,
    }))
  }
  const scaled = list.map((item) => round2((item.widthPct / sum) * 100))
  const used = scaled.slice(0, -1).reduce((total, n) => total + n, 0)
  if (scaled.length) scaled[scaled.length - 1] = round2(Math.max(1, 100 - used))
  return list.map((item, index) => ({ ...item, widthPct: scaled[index] }))
}

export function buildImageGalleryConfig(attrs = {}) {
  const mode = normalizeMode(attrs.mode)
  const gap = normalizeGap(attrs.gap)
  const rowHeight = normalizeRowHeight(attrs.rowHeight)
  const targetRowHeight = normalizeRowHeight(attrs.targetRowHeight, rowHeight)
  const columnCount = normalizeColumnCount(attrs.columnCount)
  const rows = (Array.isArray(attrs.rows) ? attrs.rows : [])
    .map((row) => {
      const items = (Array.isArray(row?.items) ? row.items : []).map(normalizeItem).filter((item) => item.src)
      if (!items.length) return null
      if (mode === 'justified') return { items }
      return { fill: true, items: normalizeRowWidths(items) }
    })
    .filter(Boolean)
  return {
    mode,
    gap,
    rowHeight,
    targetRowHeight,
    columnCount,
    rows: mode === 'justified' ? [{ items: flattenItems(rows) }] : rows,
  }
}

function galleryCssVars(config) {
  if (config.mode === 'justified') {
    return `--gallery-gap:${config.gap}px;--gallery-columns:${config.columnCount}`
  }
  return `--gallery-gap:${config.gap}px;--gallery-row-height:${config.rowHeight}px`
}

function itemFlexStyle(item, fill = true) {
  const pct = item.widthPct > 0 ? item.widthPct : 1
  const flex = !fill ? `flex:0 0 ${pct}%;min-width:0` : `flex:${pct} 1 0;min-width:0`
  const height = Number(item.heightPx)
  if (Number.isFinite(height) && height > 0) {
    return `${flex};height:${Math.round(height)}px;align-self:flex-start`
  }
  return flex
}

function itemMasonryStyle(item) {
  const height = Number(item.heightPx)
  if (Number.isFinite(height) && height > 0) return `height:${Math.round(height)}px`
  return ''
}

function itemSizedClass(item) {
  return Number(item.heightPx) > 0 ? ' is-sized' : ''
}

function renderItemHtml(item, { resolve, fill = true, masonry = false } = {}) {
  const src = escapeHtml(resolve(item.src))
  const alt = escapeHtml(item.alt || '')
  const style = masonry ? itemMasonryStyle(item) : itemFlexStyle(item, fill)
  const styleAttr = style ? ` style="${style}"` : ''
  return (
    `<div class="image-gallery__item${itemSizedClass(item)}"${styleAttr}>` +
    `<img class="image-gallery__img" src="${src}" alt="${alt}">` +
    `</div>`
  )
}

export function buildImageGalleryHtml(attrs = {}, { resolveSrc } = {}) {
  const config = buildImageGalleryConfig(attrs)
  const resolve = typeof resolveSrc === 'function' ? resolveSrc : (src) => src || ''
  const dataConfig = escapeHtml(JSON.stringify(config))
  let innerHtml
  if (config.mode === 'justified') {
    innerHtml = flattenItems(config.rows)
      .map((item) => renderItemHtml(item, { resolve, masonry: true }))
      .join('')
  } else {
    innerHtml = config.rows.map((row) => {
      const itemsHtml = row.items.map((item) => renderItemHtml(item, { resolve, fill: true })).join('')
      return `<div class="image-gallery__row">${itemsHtml}</div>`
    }).join('')
  }

  return (
    `<section data-type="${IMAGE_GALLERY_TYPE}" data-mode="${config.mode}" data-config="${dataConfig}" ` +
    `class="image-gallery image-gallery--${config.mode}" style="${galleryCssVars(config)}">` +
    `${innerHtml}</section>`
  )
}

function parseConfigAttr(element) {
  try {
    const raw = element.getAttribute('data-config')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return buildImageGalleryConfig(parsed)
  } catch {
    return null
  }
}

function parseDomStructure(element) {
  const cls = String(element.className || '')
  const mode = normalizeMode(
    element.getAttribute('data-mode')
    || (cls.includes('image-gallery--justified') ? 'justified' : 'uniform'),
  )
  const style = String(element.getAttribute('style') || '')
  const gap = Number((style.match(/--gallery-gap:\s*([\d.]+)/) || [])[1])
  const rowHeight = Number((style.match(/--gallery-row-height:\s*([\d.]+)/) || [])[1])
  const columnCount = Number((style.match(/--gallery-columns:\s*([\d.]+)/) || [])[1])
  const parseItem = (itemEl) => {
    const img = itemEl.querySelector('img')
    const styleText = String(itemEl.getAttribute('style') || '')
    const flexGrow = Number(String(itemEl.style.flexGrow || itemEl.style.flex || '').split(/\s+/)[0])
    const basisMatch = styleText.match(/flex:\s*0\s+0\s+([\d.]+)%/)
    const widthPct = Number.isFinite(Number(basisMatch?.[1]))
      ? Number(basisMatch[1])
      : (Number.isFinite(flexGrow) && flexGrow > 0 ? flexGrow : 0)
    const heightMatch = styleText.match(/height:\s*([\d.]+)px/)
    const heightPx = Number(heightMatch?.[1])
    return {
      src: img?.getAttribute('src') || '',
      alt: img?.getAttribute('alt') || '',
      widthPct,
      heightPx: Number.isFinite(heightPx) && heightPx > 0 ? heightPx : 0,
      heightLocked: Number.isFinite(heightPx) && heightPx > 0,
    }
  }
  const directItems = [...element.querySelectorAll(':scope > .image-gallery__item')].map(parseItem)
  const rows = [...element.querySelectorAll(':scope > .image-gallery__row')].map((rowEl) => ({
    items: [...rowEl.querySelectorAll(':scope > .image-gallery__item')].map(parseItem),
  }))
  return buildImageGalleryConfig({
    mode,
    gap,
    rowHeight,
    targetRowHeight: rowHeight,
    columnCount,
    rows: mode === 'justified'
      ? [{ items: directItems.length ? directItems : rows.flatMap((row) => row.items) }]
      : (rows.length ? rows : []),
  })
}

export function repairImageGalleryHtml(html, { resolveSrc } = {}) {
  if (!html || !html.includes('image-gallery')) return html
  try {
    const parsed = parseHtmlFragment(html, '__gallery_root')
    if (!parsed?.root) return html
    const { doc, root: fragment } = parsed
    fragment.querySelectorAll(`[data-type="${IMAGE_GALLERY_TYPE}"]`).forEach((root) => {
      const config = parseConfigAttr(root) || parseDomStructure(root)
      const wrap = doc.createElement('div')
      wrap.innerHTML = relocateInlineStylesInHtml(buildImageGalleryHtml(config, { resolveSrc }))
      const next = wrap.firstElementChild
      if (next) root.replaceWith(next)
    })
    return fragment.innerHTML
  } catch {
    return html
  }
}
